import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  DialogTitle,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Sheet,
  Stack,
  Table,
  Textarea,
  Typography,
} from "@mui/joy";
import DeleteIcon from "@mui/icons-material/Delete";
import Page from "../components/Pages/Page";
import PageHeader from "../components/PageHeader";
import Loading from "../components/Loading";
import AccessRequestsList from "../components/Dashboard/AccessRequestsList";
import ApproveAccessRequestModal from "../components/Dashboard/ApproveAccessRequestModal";
import RejectAccessRequestModal from "../components/Dashboard/RejectAccessRequestModal";
import { useOrgSettingsContext } from "../providers/OrgSettingsProvider";
import {
  useApproveAccessRequest,
  useInvitationSend,
  useOrganizationUsers,
  useRejectAccessRequest,
  useRemoveUserFromOrganization,
  useUpdateMemberRole,
} from "../services/api";
import { AccessRequest, User as UserType } from "../common/types";

const OrgSettingsMembers: React.FC = () => {
  const { user, organizationDetails } = useOrgSettingsContext();
  const invitationSendAPI = useInvitationSend();
  const organizationUsersAPI = useOrganizationUsers();
  const approveAccessRequestAPI = useApproveAccessRequest();
  const rejectAccessRequestAPI = useRejectAccessRequest();
  const removeUserFromOrganization = useRemoveUserFromOrganization();
  const updateMemberRole = useUpdateMemberRole();

  const [orgMembers, setOrgMembers] = useState<UserType[]>([]);
  const [loadingOrgMembers, setLoadingOrgMembers] = useState(false);
  const [accessRequests, setAccessRequests] = useState<AccessRequest[]>([]);
  const [loadingAccessRequests, setLoadingAccessRequests] = useState(false);

  // Invite state
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"admin" | "member">("member");
  const [inviteMessage, setInviteMessage] = useState("");
  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [sendInviteModalOpen, setSendInviteModalOpen] = useState(false);

  // Remove user state
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [removeUserModalOpen, setRemoveUserModalOpen] = useState(false);
  const [userToRemove, setUserToRemove] = useState<{
    id: string;
    email: string;
  } | null>(null);
  const [removeUserError, setRemoveUserError] = useState<string | null>(null);

  // Access request state
  const [approvingRequest, setApprovingRequest] = useState<string | null>(null);
  const [rejectingRequest, setRejectingRequest] = useState<string | null>(null);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [requestToApprove, setRequestToApprove] =
    useState<AccessRequest | null>(null);
  const [approveRole, setApproveRole] = useState<"admin" | "member">("member");
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [requestToReject, setRequestToReject] = useState<AccessRequest | null>(
    null,
  );
  const [rejectReason, setRejectReason] = useState("");

  const fetchMembersAndRequests = async () => {
    if (!user.organization_id) return;
    setLoadingOrgMembers(true);
    setLoadingAccessRequests(true);
    try {
      const allMembers = await organizationUsersAPI(user.organization_id);
      const requests = allMembers
        .filter((m: UserType) => m.status === "access_requested")
        .map((m: UserType) => ({
          user_id: m.id,
          email: m.email,
          organization_id: user.organization_id!,
          organization_name: organizationDetails?.name || "",
          access_request_message: m.access_request_message || null,
          access_request_date:
            m.access_request_date || new Date().toISOString(),
          status: "access_requested" as const,
        }));
      setAccessRequests(requests);
      setOrgMembers(
        allMembers.filter((m: UserType) => m.status !== "access_requested"),
      );
    } catch (err: any) {
      console.error("Failed to fetch members:", err);
    } finally {
      setLoadingOrgMembers(false);
      setLoadingAccessRequests(false);
    }
  };

  useEffect(() => {
    fetchMembersAndRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.organization_id]);

  const handleSendInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user.organization_id) return;
    setSendingInvite(true);
    setInviteError(null);
    try {
      await invitationSendAPI({
        organization_id: user.organization_id,
        email: inviteEmail,
        role: inviteRole,
        message: inviteMessage || undefined,
      });
      await fetchMembersAndRequests();
      setInviteEmail("");
      setInviteRole("member");
      setInviteMessage("");
      setSendInviteModalOpen(false);
    } catch (err: any) {
      let errorMessage = "Failed to send invitation";
      if (err.response) {
        if (typeof err.response === "string") {
          errorMessage = err.response;
        } else if (err.response.detail) {
          if (Array.isArray(err.response.detail)) {
            const errors = err.response.detail
              .map((e: any) => {
                const field =
                  e.loc && e.loc.length > 0
                    ? e.loc[e.loc.length - 1]
                    : "field";
                const message = e.msg || e.message || "Invalid value";
                const reason = e.ctx?.reason;
                return reason ? `${field}: ${reason}` : `${field}: ${message}`;
              })
              .join("; ");
            errorMessage = errors || "Validation error";
          } else if (typeof err.response.detail === "string") {
            errorMessage = err.response.detail;
          } else {
            errorMessage = JSON.stringify(err.response.detail);
          }
        } else if (err.response.message) {
          errorMessage = err.response.message;
        } else if (err.response.error) {
          errorMessage = err.response.error;
        } else if (typeof err.response === "object") {
          const values = Object.values(err.response).filter(
            (v) => typeof v === "string",
          );
          errorMessage =
            values.length > 0
              ? (values as string[]).join(", ")
              : JSON.stringify(err.response);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      setInviteError(errorMessage);
    } finally {
      setSendingInvite(false);
    }
  };

  const handleOpenRemoveUserModal = (userId: string, userEmail: string) => {
    setUserToRemove({ id: userId, email: userEmail });
    setRemoveUserError(null);
    setRemoveUserModalOpen(true);
  };

  const handleCloseRemoveUserModal = () => {
    setRemoveUserModalOpen(false);
    setUserToRemove(null);
    setRemoveUserError(null);
  };

  const handleRemoveUser = async () => {
    if (!user.organization_id || !userToRemove) return;
    setRemovingUserId(userToRemove.id);
    setRemoveUserError(null);
    try {
      await removeUserFromOrganization(user.organization_id, userToRemove.id);
      await fetchMembersAndRequests();
      handleCloseRemoveUserModal();
    } catch (err: any) {
      setRemoveUserError(
        err?.response?.detail ||
          err?.message ||
          "Failed to remove user. Please try again.",
      );
    } finally {
      setRemovingUserId(null);
    }
  };

  const handleOpenApproveModal = (request: AccessRequest) => {
    setRequestToApprove(request);
    setApproveRole("member");
    setApproveModalOpen(true);
  };

  const handleCloseApproveModal = () => {
    setApproveModalOpen(false);
    setRequestToApprove(null);
    setApproveRole("member");
  };

  const handleApproveAccessRequest = async () => {
    if (!requestToApprove || !user.organization_id) return;
    setApprovingRequest(requestToApprove.user_id);
    try {
      await approveAccessRequestAPI(user.organization_id, {
        user_id: requestToApprove.user_id,
        role: approveRole,
      });
      await fetchMembersAndRequests();
      handleCloseApproveModal();
    } catch (err: any) {
      console.error("Failed to approve access request:", err);
    } finally {
      setApprovingRequest(null);
    }
  };

  const handleOpenRejectModal = (request: AccessRequest) => {
    setRequestToReject(request);
    setRejectReason("");
    setRejectModalOpen(true);
  };

  const handleCloseRejectModal = () => {
    setRejectModalOpen(false);
    setRequestToReject(null);
    setRejectReason("");
  };

  const handleRejectAccessRequest = async () => {
    if (!requestToReject || !user.organization_id) return;
    setRejectingRequest(requestToReject.user_id);
    try {
      await rejectAccessRequestAPI(user.organization_id, {
        user_id: requestToReject.user_id,
        reason: rejectReason || undefined,
      });
      await fetchMembersAndRequests();
      handleCloseRejectModal();
    } catch (err: any) {
      console.error("Failed to reject access request:", err);
    } finally {
      setRejectingRequest(null);
    }
  };

  return (
    <Page>
      <PageHeader>
        <Box>
          <Typography level="h1">Members</Typography>
          <Typography level="body-sm" color="neutral">
            Manage organization members, invitations, and access requests
          </Typography>
        </Box>
        <Button
          variant="solid"
          color="primary"
          size="sm"
          onClick={() => setSendInviteModalOpen(true)}
          sx={{
            width: { xs: "100%", sm: "auto" },
            justifyContent: { xs: "center", sm: "flex-start" },
          }}
        >
          Invite User
        </Button>
      </PageHeader>

      <Card>
        <CardContent>
          <Stack spacing={4}>
            {/* Access Requests */}
            <Box>
              <Typography level="title-lg" sx={{ mb: 2, fontWeight: 600 }}>
                Access Requests
              </Typography>
              <AccessRequestsList
                accessRequests={accessRequests}
                loading={loadingAccessRequests}
                approvingRequest={approvingRequest}
                rejectingRequest={rejectingRequest}
                onApprove={handleOpenApproveModal}
                onReject={handleOpenRejectModal}
              />
            </Box>

            <Divider />

            {/* Organization Members */}
            <Box>
              <Typography level="title-lg" sx={{ mb: 2, fontWeight: 600 }}>
                Organization Members
              </Typography>

              {loadingOrgMembers ? (
                <Box sx={{ py: 3, textAlign: "center" }}>
                  <Loading />
                </Box>
              ) : orgMembers.length === 0 ? (
                <Box sx={{ py: 3, textAlign: "center" }}>
                  <Typography level="body-sm" color="neutral">
                    No members found.
                  </Typography>
                </Box>
              ) : (
                <Sheet
                  variant="outlined"
                  sx={{ borderRadius: "sm", overflow: "auto" }}
                >
                  <Table stickyHeader>
                    <thead>
                      <tr>
                        <th
                          style={{
                            width: "30%",
                            backgroundColor:
                              "var(--joy-palette-background-surface)",
                          }}
                        >
                          Email
                        </th>
                        <th
                          style={{
                            width: "15%",
                            backgroundColor:
                              "var(--joy-palette-background-surface)",
                          }}
                        >
                          Role
                        </th>
                        <th
                          style={{
                            width: "15%",
                            backgroundColor:
                              "var(--joy-palette-background-surface)",
                          }}
                        >
                          Status
                        </th>
                        <th
                          style={{
                            width: "15%",
                            backgroundColor:
                              "var(--joy-palette-background-surface)",
                          }}
                        >
                          Joined
                        </th>
                        <th
                          style={{
                            width: "25%",
                            backgroundColor:
                              "var(--joy-palette-background-surface)",
                          }}
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {orgMembers.map((member) => (
                        <tr key={member.id}>
                          <td>{member.email}</td>
                          <td>
                            {member.id === user.id ? (
                              <Chip size="sm" variant="soft" color="primary">
                                Administrator
                              </Chip>
                            ) : (
                              <Select
                                size="sm"
                                variant="soft"
                                value={member.role_in_org || "member"}
                                onChange={async (_e, newRole) => {
                                  if (!newRole || !user.organization_id) return;
                                  try {
                                    await updateMemberRole(
                                      user.organization_id,
                                      member.id,
                                      newRole,
                                    );
                                    setOrgMembers((prev) =>
                                      prev.map((m) =>
                                        m.id === member.id
                                          ? { ...m, role_in_org: newRole }
                                          : m,
                                      ),
                                    );
                                  } catch {
                                    // authFetch already shows error toast
                                  }
                                }}
                                sx={{ minWidth: 130 }}
                              >
                                <Option value="member">Member</Option>
                                <Option value="admin">Administrator</Option>
                              </Select>
                            )}
                          </td>
                          <td>
                            <Chip
                              size="sm"
                              variant="soft"
                              color={
                                member.status === "verified"
                                  ? "success"
                                  : member.status === "invited"
                                    ? "warning"
                                    : member.status === "suspended"
                                      ? "danger"
                                      : "neutral"
                              }
                            >
                              {member.status === "verified"
                                ? "Active"
                                : member.status === "invited"
                                  ? "Invited"
                                  : member.status === "suspended"
                                    ? "Suspended"
                                    : member.status}
                            </Chip>
                          </td>
                          <td>
                            <Typography level="body-sm" color="neutral">
                              {(member as any).date_created
                                ? new Date(
                                    (member as any).date_created,
                                  ).toLocaleDateString()
                                : "N/A"}
                            </Typography>
                          </td>
                          <td>
                            {member.id !== user.id && (
                              <Button
                                variant="outlined"
                                color="danger"
                                size="sm"
                                onClick={() =>
                                  handleOpenRemoveUserModal(
                                    member.id,
                                    member.email,
                                  )
                                }
                                startDecorator={<DeleteIcon />}
                              >
                                Remove
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Sheet>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Send Invitation Modal */}
      <Modal
        open={sendInviteModalOpen}
        onClose={() => {
          setSendInviteModalOpen(false);
          setInviteEmail("");
          setInviteRole("member");
          setInviteMessage("");
          setInviteError(null);
        }}
      >
        <ModalDialog
          size="md"
          variant="outlined"
          sx={{ maxWidth: 600, width: "100%" }}
        >
          <DialogTitle>Send Invitation</DialogTitle>
          <form onSubmit={handleSendInvitation}>
            <Stack spacing={3}>
              {inviteError && (
                <Alert color="danger" variant="soft">
                  {inviteError}
                </Alert>
              )}
              <FormControl required>
                <FormLabel>Email Address</FormLabel>
                <Input
                  type="email"
                  placeholder="user@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  disabled={sendingInvite}
                  size="md"
                  autoFocus
                />
              </FormControl>
              <FormControl required>
                <FormLabel>Role</FormLabel>
                <Select
                  value={inviteRole}
                  onChange={(_, value) =>
                    setInviteRole(value as "admin" | "member")
                  }
                  disabled={sendingInvite}
                  size="md"
                >
                  <Option value="member">Member</Option>
                  <Option value="admin">Administrator</Option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Message (Optional)</FormLabel>
                <Textarea
                  placeholder="Add a personal message..."
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  disabled={sendingInvite}
                  size="md"
                  minRows={3}
                />
              </FormControl>
              <Stack
                direction="row"
                spacing={2}
                sx={{ justifyContent: "flex-end", mt: 2 }}
              >
                <Button
                  variant="outlined"
                  color="neutral"
                  onClick={() => {
                    setSendInviteModalOpen(false);
                    setInviteEmail("");
                    setInviteRole("member");
                    setInviteMessage("");
                    setInviteError(null);
                  }}
                  disabled={sendingInvite}
                  size="md"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="solid"
                  color="primary"
                  disabled={sendingInvite || !inviteEmail.trim()}
                  loading={sendingInvite}
                  size="md"
                >
                  Send Invitation
                </Button>
              </Stack>
            </Stack>
          </form>
        </ModalDialog>
      </Modal>

      {/* Remove User Modal */}
      <Modal open={removeUserModalOpen} onClose={handleCloseRemoveUserModal}>
        <ModalDialog
          size="md"
          variant="outlined"
          sx={{ maxWidth: 400, width: "100%" }}
        >
          <DialogTitle>Remove User</DialogTitle>
          <Stack spacing={3}>
            {removeUserError && (
              <Alert color="danger" variant="soft">
                {removeUserError}
              </Alert>
            )}
            <Typography level="body-md">
              Are you sure you want to remove{" "}
              <strong>{userToRemove?.email}</strong> from the organization? This
              action cannot be undone.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                color="neutral"
                onClick={handleCloseRemoveUserModal}
                disabled={removingUserId === userToRemove?.id}
                size="md"
              >
                Cancel
              </Button>
              <Button
                variant="solid"
                color="danger"
                onClick={handleRemoveUser}
                loading={removingUserId === userToRemove?.id}
                disabled={removingUserId === userToRemove?.id}
                size="md"
              >
                Remove User
              </Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>

      <ApproveAccessRequestModal
        open={approveModalOpen}
        onClose={handleCloseApproveModal}
        request={requestToApprove}
        approveRole={approveRole}
        onApproveRoleChange={setApproveRole}
        onApprove={handleApproveAccessRequest}
        approving={approvingRequest === requestToApprove?.user_id}
      />

      <RejectAccessRequestModal
        open={rejectModalOpen}
        onClose={handleCloseRejectModal}
        request={requestToReject}
        rejectReason={rejectReason}
        onRejectReasonChange={setRejectReason}
        onReject={handleRejectAccessRequest}
        rejecting={rejectingRequest === requestToReject?.user_id}
      />
    </Page>
  );
};

export default OrgSettingsMembers;
